import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Trash2, Edit2, MapPin, Users, Calendar, Search, Map } from 'lucide-react';
import { primaryButton, ghostButton, inputStyle, glass, glassLight, T, Badge, StatCard, EmptyState, Field } from '../ui';
interface Cliente {
  id: string;
  codigo: string;
  nombre: string;
  ubicacion: string;
  telefono: string;
}
interface RutaCliente {
  clienteId: string;
  clienteNombre: string;
  codigo: string;
  ubicacion: string;
  estado: 'pendiente' | 'visitado' | 'sin_compra';
}
interface Ruta {
  id: string;
  nombre: string;
  zona: string;
  supervisor: string;
  clientes: RutaCliente[];
  fechaCreacion: string;
  diasSemana: string[];
  estado: 'activa' | 'inactiva';
}
interface RutasProps {
  rutas: Ruta[];
  clientes: Cliente[];
  onAddRuta: (ruta: Ruta) => void;
  onDeleteRuta: (id: string) => void;
  onUpdateRuta: (ruta: Ruta) => void;
}
const DIAS_SEMANA = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
const visitaColor = (estado: RutaCliente['estado']) => {
  if (estado === 'visitado') return {
    color: T.ok,
    soft: T.okSoft
  };
  if (estado === 'sin_compra') return {
    color: T.warn,
    soft: T.warnSoft
  };
  return {
    color: T.textMuted,
    soft: T.surfaceSubtle
  };
};
const iconBtn: React.CSSProperties = {
  width: 30,
  height: 30,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: 8,
  border: `1px solid ${T.border}`,
  background: T.surface,
  cursor: 'pointer'
};
export function Rutas({
  rutas,
  clientes,
  onAddRuta,
  onDeleteRuta,
  onUpdateRuta
}: RutasProps) {
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterEstado, setFilterEstado] = useState<'todas' | 'activa' | 'inactiva'>('activa');
  const [formData, setFormData] = useState<Partial<Ruta>>({
    estado: 'activa',
    clientes: [],
    diasSemana: []
  });
  const [selectedClientes, setSelectedClientes] = useState<string[]>([]);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.nombre || !formData.zona || !formData.supervisor) {
      alert('Por favor completa los campos obligatorios');
      return;
    }
    const rutaClientes: RutaCliente[] = selectedClientes.map(clienteId => {
      const cliente = clientes.find(c => c.id === clienteId);
      return {
        clienteId,
        clienteNombre: cliente?.nombre || '',
        codigo: cliente?.codigo || '',
        ubicacion: cliente?.ubicacion || '',
        estado: 'pendiente'
      };
    });
    if (editingId) {
      onUpdateRuta({
        ...formData,
        id: editingId,
        clientes: rutaClientes
      } as Ruta);
      setEditingId(null);
    } else {
      onAddRuta({
        ...formData,
        id: Date.now().toString(),
        clientes: rutaClientes,
        fechaCreacion: new Date().toISOString().split('T')[0]
      } as Ruta);
    }
    setFormData({
      estado: 'activa',
      clientes: [],
      diasSemana: []
    });
    setSelectedClientes([]);
    setShowForm(false);
  };
  const handleEdit = (ruta: Ruta) => {
    setFormData(ruta);
    setSelectedClientes(ruta.clientes.map(c => c.clienteId));
    setEditingId(ruta.id);
    setShowForm(true);
  };
  const toggleCliente = (clienteId: string) => {
    setSelectedClientes(prev => prev.includes(clienteId) ? prev.filter(id => id !== clienteId) : [...prev, clienteId]);
  };
  const toggleDia = (dia: string) => {
    setFormData(prev => ({
      ...prev,
      diasSemana: prev.diasSemana?.includes(dia) ? prev.diasSemana.filter(d => d !== dia) : [...(prev.diasSemana || []), dia]
    }));
  };
  const filteredRutas = rutas.filter(r => {
    const matchSearch = r.nombre.toLowerCase().includes(searchTerm.toLowerCase()) || r.zona.toLowerCase().includes(searchTerm.toLowerCase()) || r.supervisor.toLowerCase().includes(searchTerm.toLowerCase());
    const matchEstado = filterEstado === 'todas' || r.estado === filterEstado;
    return matchSearch && matchEstado;
  });
  const rutasActivas = rutas.filter(r => r.estado === 'activa').length;
  return <div style={{
    display: 'flex',
    flexDirection: 'column',
    gap: 20
  }} data-id="element-772">
      {/* Header Stats */}
      <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
      gap: 14
    }} data-id="element-773">
        
        <StatCard label="Total rutas" value={String(rutas.length)} note="Registradas en el sistema" accent={T.info} data-id="element-774" />
        
        <StatCard label="Activas" value={String(rutasActivas)} note="En operación" accent={T.ok} data-id="element-775" />
        
        <StatCard label="Total clientes" value={String(rutas.reduce((sum, r) => sum + r.clientes.length, 0))} note="Asignados a rutas" accent={T.warn} data-id="element-776" />
        
      </div>

      {/* Controles */}
      <div style={{
      ...glassLight,
      padding: 16
    }} data-id="element-777">
        <div style={{
        display: 'flex',
        gap: 10,
        flexWrap: 'wrap',
        alignItems: 'center'
      }} data-id="element-778">
          
          <div style={{
          flex: '1 1 240px',
          position: 'relative'
        }} data-id="element-779">
            <Search size={16} style={{
            position: 'absolute',
            left: 12,
            top: '50%',
            transform: 'translateY(-50%)',
            color: T.textMuted
          }} data-id="element-780" />
            
            <input type="text" placeholder="Buscar ruta, zona o supervisor..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} style={{
            ...inputStyle,
            paddingLeft: 36
          }} data-id="element-781" />
            
          </div>
          <select value={filterEstado} onChange={e => setFilterEstado(e.target.value as any)} style={{
          ...inputStyle,
          width: 'auto'
        }} data-id="element-782">
            
            <option value="todas" data-id="element-783">Todas</option>
            <option value="activa" data-id="element-784">Activas</option>
            <option value="inactiva" data-id="element-785">Inactivas</option>
          </select>
          <button onClick={() => {
          setShowForm(!showForm);
          setEditingId(null);
          setFormData({
            estado: 'activa',
            clientes: [],
            diasSemana: []
          });
          setSelectedClientes([]);
        }} style={{
          ...primaryButton,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6
        }} data-id="element-786">
            
            <Plus size={15} data-id="element-787" /> Nueva Ruta
          </button>
        </div>
      </div>

      {/* Formulario */}
      <AnimatePresence data-id="element-788">
        {showForm && <motion.div initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} exit={{
        opacity: 0,
        height: 0
      }} transition={{
        duration: 0.2
      }} style={{
        overflow: 'hidden'
      }} data-id="element-789">
          
            <div style={{
          ...glass,
          padding: 20
        }} data-id="element-790">
              <h3 style={{
            fontSize: 15,
            fontWeight: 800,
            marginBottom: 16,
            color: T.text
          }} data-id="element-791">
              
                {editingId ? 'Editar Ruta' : 'Nueva Ruta'}
              </h3>
              <form onSubmit={handleSubmit} data-id="element-792">
                <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 14,
              marginBottom: 18
            }} data-id="element-793">
                
                  <Field label="Nombre de ruta *" data-id="element-794">
                    <input type="text" value={formData.nombre || ''} onChange={e => setFormData({
                  ...formData,
                  nombre: e.target.value
                })} style={inputStyle} placeholder="Ruta Centro" data-id="element-795" />
                  
                  </Field>
                  <Field label="Zona *" data-id="element-796">
                    <input type="text" value={formData.zona || ''} onChange={e => setFormData({
                  ...formData,
                  zona: e.target.value
                })} style={inputStyle} placeholder="Centro / Este / Norte" data-id="element-797" />
                  
                  </Field>
                  <Field label="Supervisor *" data-id="element-798">
                    <input type="text" value={formData.supervisor || ''} onChange={e => setFormData({
                  ...formData,
                  supervisor: e.target.value
                })} style={inputStyle} placeholder="Nombre del supervisor" data-id="element-799" />
                  
                  </Field>
                  <Field label="Estado" data-id="element-800">
                    <select value={formData.estado || 'activa'} onChange={e => setFormData({
                  ...formData,
                  estado: e.target.value as any
                })} style={inputStyle} data-id="element-801">
                    
                      <option value="activa" data-id="element-802">Activa</option>
                      <option value="inactiva" data-id="element-803">Inactiva</option>
                    </select>
                  </Field>
                </div>

                <Field label="Días de operación" data-id="element-804">
                  <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: 8
              }} data-id="element-805">
                  
                    {DIAS_SEMANA.map(dia => {
                  const checked = formData.diasSemana?.includes(dia) || false;
                  return <label key={dia} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    cursor: 'pointer',
                    padding: '7px 10px',
                    borderRadius: 8,
                    border: `1px solid ${checked ? T.accentBorder : T.border}`,
                    background: checked ? T.accentSoft : T.surface
                  }} data-id="element-806">
                        
                          <input type="checkbox" checked={checked} onChange={() => toggleDia(dia)} style={{
                      width: 15,
                      height: 15,
                      accentColor: T.accent
                    }} data-id="element-807" />
                        
                          <span style={{
                      fontSize: 13,
                      fontWeight: 600,
                      color: checked ? T.accent : T.textSecondary
                    }} data-id="element-808">
                          
                            {dia}
                          </span>
                        </label>;
                })}
                  </div>
                </Field>

                <Field label={`Clientes en esta ruta (${selectedClientes.length} seleccionados)`} data-id="element-809">
                  <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 6,
                maxHeight: 260,
                overflowY: 'auto',
                background: T.surfaceSubtle,
                border: `1px solid ${T.border}`,
                borderRadius: 10,
                padding: 10
              }} data-id="element-810">
                  
                    {clientes.length === 0 ? <p style={{
                  gridColumn: '1 / -1',
                  color: T.textMuted,
                  fontSize: 13,
                  margin: 0
                }} data-id="element-811">
                    
                        No hay clientes disponibles
                      </p> : clientes.map(cliente => {
                  const checked = selectedClientes.includes(cliente.id);
                  return <label key={cliente.id} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    cursor: 'pointer',
                    padding: '7px 9px',
                    borderRadius: 8,
                    background: checked ? T.accentSoft : 'transparent'
                  }} data-id="element-812">
                        
                            <input type="checkbox" checked={checked} onChange={() => toggleCliente(cliente.id)} style={{
                      width: 15,
                      height: 15,
                      accentColor: T.accent
                    }} data-id="element-813" />
                        

                            <span style={{
                      fontSize: 13,
                      color: checked ? T.accent : T.textSecondary,
                      fontWeight: checked ? 700 : 500
                    }} data-id="element-814">
                          
                              {cliente.nombre}
                            </span>
                          </label>;
                })}
                  </div>
                </Field>

                <div style={{
              display: 'flex',
              gap: 8,
              marginTop: 16
            }} data-id="element-815">
                  <button type="submit" style={primaryButton} data-id="element-816">
                    {editingId ? 'Actualizar' : 'Crear'} ruta
                  </button>
                  <button type="button" onClick={() => {
                setShowForm(false);
                setEditingId(null);
                setSelectedClientes([]);
              }} style={ghostButton} data-id="element-817">
                  
                    Cancelar
                  </button>
                </div>
              </form>
            </div>
          </motion.div>}
      </AnimatePresence>

      {/* Lista de Rutas */}
      {filteredRutas.length === 0 ? <div style={{
      ...glass,
      padding: 20
    }} data-id="element-818">
          <EmptyState icon={<Map size={20} data-id="element-820" />} title="No hay rutas disponibles" hint="Crea una ruta nueva o ajusta los filtros de búsqueda." data-id="element-819" />
        
        </div> : <div style={{
      display: 'flex',
      flexDirection: 'column',
      gap: 14
    }} data-id="element-821">
          {filteredRutas.map(ruta => <div key={ruta.id} style={{
        ...glass,
        padding: 20
      }} data-id="element-822">
              <div style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 12,
          flexWrap: 'wrap',
          marginBottom: 14
        }} data-id="element-823">
          
                <div data-id="element-824">
                  <h3 style={{
              fontSize: 16,
              fontWeight: 800,
              color: T.text,
              margin: '0 0 8px'
            }} data-id="element-825">
              
                    {ruta.nombre}
                  </h3>
                  <div style={{
              display: 'flex',
              gap: 16,
              flexWrap: 'wrap',
              fontSize: 12.5,
              color: T.textSecondary
            }} data-id="element-826">
              
                    <span style={{
                display: 'flex',
                alignItems: 'center',
                gap: 5
              }} data-id="element-827">
                
                      <MapPin size={13} data-id="element-828" /> {ruta.zona}
                    </span>
                    <span style={{
                display: 'flex',
                alignItems: 'center',
                gap: 5
              }} data-id="element-829">
                
                      <Users size={13} data-id="element-830" /> Supervisor: {ruta.supervisor}
                    </span>
                    <span style={{
                display: 'flex',
                alignItems: 'center',
                gap: 5
              }} data-id="element-831">
                
                      <Calendar size={13} data-id="element-832" />{' '}
                      {ruta.diasSemana?.join(', ') || 'Sin días asignados'}
                    </span>
                  </div>
                </div>
                <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8
          }} data-id="element-833">
                  <Badge label={ruta.estado} color={ruta.estado === 'activa' ? T.ok : T.danger} soft={ruta.estado === 'activa' ? T.okSoft : T.dangerSoft} data-id="element-834" />
              
                  <button onClick={() => handleEdit(ruta)} title="Editar" style={{
              ...iconBtn,
              color: T.info
            }} data-id="element-835">
              
                    <Edit2 size={14} data-id="element-836" />
                  </button>
                  <button onClick={() => onDeleteRuta(ruta.id)} title="Eliminar" style={{
              ...iconBtn,
              color: T.danger
            }} data-id="element-837">
              
                    <Trash2 size={14} data-id="element-838" />
                  </button>
                </div>
              </div>

              <div style={{
          borderTop: `1px solid ${T.border}`,
          paddingTop: 14
        }} data-id="element-839">
                <h4 style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            color: T.textMuted,
            margin: '0 0 10px'
          }} data-id="element-840">
            
                  Clientes asignados ({ruta.clientes.length})
                </h4>
                {ruta.clientes.length === 0 ? <p style={{
            fontSize: 13,
            color: T.textMuted,
            margin: 0
          }} data-id="element-841">
                    Sin clientes asignados
                  </p> : <div style={{
            overflowX: 'auto'
          }} data-id="element-842">
                    <table style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: 12.5
            }} data-id="element-843">
              
                      <thead data-id="element-844">
                        <tr data-id="element-845">
                          {['Código', 'Nombre', 'Ubicación', 'Estado visita'].map(h => <th key={h} style={{
                    textAlign: 'left',
                    padding: '0 8px 8px',
                    fontSize: 10.5,
                    fontWeight: 700,
                    letterSpacing: '0.04em',
                    textTransform: 'uppercase',
                    color: T.textMuted,
                    borderBottom: `1px solid ${T.border}`
                  }} data-id="element-846">
                      
                              {h}
                            </th>)}
                        </tr>
                      </thead>
                      <tbody data-id="element-847">
                        {ruta.clientes.map((cliente, idx) => {
                  const vc = visitaColor(cliente.estado);
                  return <tr key={idx} data-id="element-848">
                              <td style={{
                      padding: '8px',
                      borderBottom: `1px solid ${T.border}`,
                      fontFamily: 'monospace',
                      color: T.info,
                      fontWeight: 700
                    }} data-id="element-849">
                        
                                {cliente.codigo}
                              </td>
                              <td style={{
                      padding: '8px',
                      borderBottom: `1px solid ${T.border}`,
                      color: T.text
                    }} data-id="element-850">
                        
                                {cliente.clienteNombre}
                              </td>
                              <td style={{
                      padding: '8px',
                      borderBottom: `1px solid ${T.border}`,
                      color: T.textSecondary
                    }} data-id="element-851">
                        
                                {cliente.ubicacion}
                              </td>
                              <td style={{
                      padding: '8px',
                      borderBottom: `1px solid ${T.border}`
                    }} data-id="element-852">
                        
                                <Badge label={cliente.estado} color={vc.color} soft={vc.soft} dot={false} data-id="element-853" />
                        
                              </td>
                            </tr>;
                })}
                      </tbody>
                    </table>
                  </div>}
              </div>
            </div>)}
        </div>}
    </div>;
}